import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orderKeys } from '@/services/queryKeys';
import { orderService } from '@/services/order.service';
import {
  isPosAccountingPending,
  POS_ACCOUNTING_POLL_MS,
  POS_ACCOUNTING_POLL_TIMEOUT_MS,
  type PosAccountingProbe,
} from '@/utils/posFulfillment';

export function useOrderDetail(id: string | undefined) {
  // TCR-1443: mốc bắt đầu poll giữ trong ref chứ không trong state — đổi nó
  // KHÔNG được kéo theo một vòng render, nếu không mỗi nhịp poll lại render thừa.
  const pollStartedAt = useRef<number | null>(null);

  return useQuery({
    queryKey: id ? orderKeys.detail(id) : ['orders', 'detail', 'none'],
    queryFn: () => orderService.getById(id!),
    enabled: !!id,
    retry: 0,
    // Hạch toán fulfillment POS chạy ở worker, xong SAU khi request bấm nút đã
    // trả về — xem `@/utils/posFulfillment`. Poll cho tới khi nó kết thúc để
    // chứng từ tự hiện, không bắt người dùng reload trang.
    refetchInterval: (query) => {
      const order = query.state.data as PosAccountingProbe | undefined;
      if (!isPosAccountingPending(order)) {
        // Reset để mốc hạch toán kế tiếp trên cùng đơn lại được trọn vẹn cửa sổ
        // poll của nó.
        pollStartedAt.current = null;
        return false;
      }
      const now = Date.now();
      if (pollStartedAt.current === null) pollStartedAt.current = now;
      if (now - pollStartedAt.current > POS_ACCOUNTING_POLL_TIMEOUT_MS) return false;
      return POS_ACCOUNTING_POLL_MS;
    },
  });
}

export function useOrderAuditHistory(id: string | undefined, page: number = 1, pageSize: number = 20) {
  return useQuery({
    queryKey: ['orders', 'auditHistory', id, page, pageSize],
    queryFn: () => orderService.getAuditHistory(id!, page, pageSize),
    enabled: !!id,
  });
}
